import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { getAllSlips, Slip } from '../../../firebase/services/slipService';
import { getAllCustomers, Customer } from '../../../firebase/services/customerVesselService';
import { useNavigate } from 'react-router-dom';

type Order = 'asc' | 'desc';

interface HeadCell {
  id: string;
  label: string;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: 'slipNumber', label: 'Slip Number', sortable: true },
  { id: 'customerId', label: 'Customer', sortable: true },
  { id: 'assignedVesselId', label: 'Vessel', sortable: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'type', label: 'Type', sortable: true },
  { id: 'length', label: 'Length', sortable: true },
  { id: 'width', label: 'Width', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false }
];

const SlipListView: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [slips, setSlips] = useState<Slip[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredSlips, setFilteredSlips] = useState<Slip[]>([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Slip>('slipNumber');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [slipData, customerData] = await Promise.all([
          getAllSlips(),
          getAllCustomers()
        ]);
        setSlips(slipData);
        setCustomers(customerData);
        filterSlips(slipData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const filterSlips = (slipData: Slip[]) => {
    const filter = searchParams.get('filter');
    let filtered = [...slipData];

    switch (filter) {
      case 'rented':
        filtered = slipData.filter(slip => !!slip.customerId);
        break;
      case 'occupied':
        filtered = slipData.filter(slip => !!slip.assignedVesselId);
        break;
      case 'available':
        filtered = slipData.filter(slip => !slip.customerId);
        break;
      default:
        filtered = slipData;
    }

    setFilteredSlips(filtered);
  };

  const handleRequestSort = (property: string) => {
    if (property === 'actions') return;
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof Slip);
  };

  const sortedSlips = React.useMemo(() => {
    return [...filteredSlips].sort((a, b) => {
      if (orderBy === 'slipNumber') {
        // Custom sorting for slip numbers (e.g., "A-1" comes before "A-10")
        const aParts = a.slipNumber.split('-');
        const bParts = b.slipNumber.split('-');
        
        if (aParts[0] !== bParts[0]) {
          return order === 'asc' ? aParts[0].localeCompare(bParts[0]) : bParts[0].localeCompare(aParts[0]);
        }
        
        const aNum = parseInt(aParts[1]);
        const bNum = parseInt(bParts[1]);
        return order === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Only sort if orderBy is a property of slip
      if (!(orderBy in a) || !(orderBy in b)) return 0;
      const aValue = a[orderBy as keyof Slip];
      const bValue = b[orderBy as keyof Slip];

      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      return order === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredSlips, order, orderBy]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'maintenance':
        return 'warning';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCustomerName = (customerId: string | undefined) => {
    if (!customerId) return 'N/A';
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return 'N/A';
    return customer.companyName 
      ? `${customer.companyName} (${customer.firstName} ${customer.lastName})`
      : `${customer.firstName} ${customer.lastName}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {searchParams.get('filter') === 'rented' ? 'Rented Slips' :
         searchParams.get('filter') === 'occupied' ? 'Occupied Slips' :
         searchParams.get('filter') === 'available' ? 'Available Slips' :
         'All Slips'}
      </Typography>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  {headCell.sortable ? (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedSlips.map((slip) => (
              <TableRow key={slip.id}>
                <TableCell>{slip.slipNumber}</TableCell>
                <TableCell>{getCustomerName(slip.customerId)}</TableCell>
                <TableCell>{slip.assignedVesselId || 'N/A'}</TableCell>
                <TableCell>
                  <Chip 
                    label={slip.status} 
                    color={getStatusColor(slip.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{slip.type}</TableCell>
                <TableCell>{slip.length} ft</TableCell>
                <TableCell>{slip.width} ft</TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/slips/${slip.id}`)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Slip">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/slips/edit/${slip.id}`)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SlipListView; 